import { t } from "@lingui/macro";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState, useEffect } from "react";

import { YearSelect } from "./year-select";

interface MonthYearDatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  /** When false, hide the end date controls and emit only the start month/year */
  showEnd?: boolean;
  disabled?: boolean;
}

const PRESENT = "Present";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

type ParsedDatePart = { month: string; year: string };

/** Resolves a word to a full month name, tolerating short forms ("Jun") and abbreviations ("Sept"). */
const findMonth = (token: string): string => {
  const lower = token.trim().toLowerCase();
  if (lower.length < 3) return "";

  const exact = MONTHS.findIndex((month) => month.toLowerCase() === lower);
  if (exact !== -1) return MONTHS[exact];

  const short = MONTHS_SHORT.findIndex((month) => month.toLowerCase() === lower);
  if (short !== -1) return MONTHS[short];

  const prefixed = MONTHS.findIndex((month) => month.toLowerCase().startsWith(lower));
  return prefixed === -1 ? "" : MONTHS[prefixed];
};

/**
 * Pulls a month + year out of one side of a stored date. Deliberately lenient so an
 * already-saved value is shown instead of silently dropped: "2020", "Jun 2023",
 * "June 2023", "Jun'23", "Since August 2024" and "Present" all parse.
 */
const parseDatePart = (raw?: string): ParsedDatePart => {
  const part = (raw ?? "").trim();
  if (!part) return { month: "", year: "" };
  if (/present|current|ongoing/i.test(part)) return { month: "", year: PRESENT };

  const month = (part.match(/[A-Za-z]+/g) ?? []).map(findMonth).find(Boolean) ?? "";

  const fourDigitYear = part.match(/\d{4}/)?.[0];
  if (fourDigitYear) return { month, year: fourDigitYear };

  // Two-digit years such as "Jun'23"
  const twoDigitYear = part.match(/'(\d{2})\b/)?.[1] ?? part.match(/\b(\d{2})\b/)?.[1];
  if (twoDigitYear) {
    const sameCentury = 2000 + Number(twoDigitYear);
    const year = sameCentury > new Date().getFullYear() ? 1900 + Number(twoDigitYear) : sameCentury;
    return { month, year: String(year) };
  }

  return { month, year: "" };
};

/**
 * Splits a stored date into start/end. Handles the canonical " - " alongside legacy
 * separators still present in older resumes: en/em dashes and hyphens without spaces
 * (e.g. "2020 – 2024", "2017-2021", "2016 to 2018").
 */
const splitDateRange = (value: string): [string, string] => {
  const spaced = value.split(/\s+(?:[-–—]|to)\s+/i);
  if (spaced.length >= 2) return [spaced[0], spaced.slice(1).join(" ")];

  const compact = value.match(/^\s*(.+?)\s*[-–—]\s*(\d{4}|present|current)\s*$/i);
  if (compact) return [compact[1], compact[2]];

  return [value, ""];
};

const formatMonthYear = (month: string, year: string) => {
  if (!year) return "";
  if (year === PRESENT) return PRESENT;
  if (!month) return year;
  const monthIndex = MONTHS.indexOf(month);
  if (monthIndex === -1) return year;
  return `${MONTHS_SHORT[monthIndex]} ${year}`;
};

export const MonthYearDatePicker = ({ value, onChange, className, showEnd = true, disabled = false }: MonthYearDatePickerProps) => {
  const [startMonth, setStartMonth] = useState<string>("");
  const [startYear, setStartYear] = useState<string>("");
  const [endMonth, setEndMonth] = useState<string>("");
  const [endYear, setEndYear] = useState<string>(PRESENT);

  // Parse the stored value on mount and whenever it changes. Parsing is intentionally
  // forgiving: legacy resumes hold shapes like "2020 – 2024" (en dash), "2017-2021"
  // (no spaces) and "Since August 2024", and those must still populate the controls
  // rather than falling back to the placeholder.
  useEffect(() => {
    const raw = (value ?? "").trim();

    if (!raw) {
      // Nothing stored (e.g. the dialog was reopened for a different item), so clear
      // the controls instead of showing a date the form no longer holds.
      setStartMonth("");
      setStartYear("");
      setEndMonth("");
      setEndYear(PRESENT);
      return;
    }

    const [rawStart, rawEnd] = splitDateRange(raw);
    const start = parseDatePart(rawStart);
    const startIsPresent = start.year === PRESENT;

    setStartMonth(startIsPresent ? "" : start.month);
    setStartYear(startIsPresent ? "" : start.year);

    if (!showEnd) {
      // Single-date usage (e.g. certifications): collapse any stored range to its start.
      const formattedStart = startIsPresent ? "" : formatMonthYear(start.month, start.year);
      if (formattedStart && formattedStart !== raw) onChange(formattedStart);
      return;
    }

    const end = rawEnd ? parseDatePart(rawEnd) : { month: "", year: PRESENT };

    if (!end.year || end.year === PRESENT) {
      setEndMonth("");
      setEndYear(PRESENT);
    } else {
      setEndMonth(end.month);
      setEndYear(end.year);
    }
  }, [value]);

  // Generate year options up to the current year only.
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 51 }, (_, i) => String(currentYear - i));
  const endYearOptions = [
    PRESENT,
    ...years.filter((year) => !startYear || Number(year) >= Number(startYear)),
  ];

  const handleStartMonthChange = (month: string) => {
    setStartMonth(month);
    const startDate = formatMonthYear(month, startYear);
    if (!showEnd) {
      if (startDate) onChange(startDate);
      return;
    }
    const endDate = formatMonthYear(endMonth, endYear);
    if (startDate && endDate) {
      onChange(`${startDate} - ${endDate}`);
    } else if (startDate) {
      onChange(startDate);
    }
  };

  const handleStartYearChange = (year: string) => {
    setStartYear(year);
    if (!showEnd) {
      const startDate = formatMonthYear(startMonth, year);
      if (startDate) onChange(startDate);
      return;
    }

    if (endYear !== PRESENT && endYear && Number(endYear) < Number(year)) {
      setEndYear(year);
    }

    const nextEndYear =
      endYear !== PRESENT && endYear && Number(endYear) < Number(year) ? year : endYear;
    const startDate = formatMonthYear(startMonth, year);
    const endDate = formatMonthYear(endMonth, nextEndYear);
    if (startDate && endDate) {
      onChange(`${startDate} - ${endDate}`);
    } else if (startDate) {
      onChange(startDate);
    }
  };

  const handleEndMonthChange = (month: string) => {
    setEndMonth(month);
    const startDate = formatMonthYear(startMonth, startYear);
    const endDate = formatMonthYear(month, endYear);
    if (startDate && endDate) {
      onChange(`${startDate} - ${endDate}`);
    }
  };

  const handleEndYearChange = (year: string) => {
    if (!startYear && year !== PRESENT) {
      // No start year yet, so treat the pick as the start of the range. Without this the
      // selection never reaches the form and is lost on save (YearDatePicker does the same).
      setStartYear(year);
      setEndMonth("");
      setEndYear(PRESENT);
      onChange(`${formatMonthYear(startMonth, year)} - ${PRESENT}`);
      return;
    }

    setEndYear(year);
    if (year === PRESENT) {
      // Clear end month when Present is selected
      setEndMonth("");
    }
    const startDate = formatMonthYear(startMonth, startYear);
    const endDate = formatMonthYear(endMonth, year);
    if (startDate && endDate) {
      onChange(`${startDate} - ${endDate}`);
    } else if (startDate && year === PRESENT) {
      onChange(`${startDate} - ${PRESENT}`);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex gap-2 items-center flex-wrap">
        <div className="flex gap-2 items-center flex-1 min-w-[200px]">
          <Select value={startMonth} onValueChange={handleStartMonthChange} disabled={disabled}>
            <SelectTrigger className="flex-1 whitespace-nowrap">
              <SelectValue placeholder="Start Month" />
            </SelectTrigger>
            <SelectContent className="[&>div]:max-h-[300px] [&>div]:overflow-auto">
              {MONTHS.map((month) => (
                <SelectItem key={month} value={month}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <YearSelect
            value={startYear}
            onValueChange={handleStartYearChange}
            options={years}
            placeholder="Start Year"
            searchPlaceholder="Search year"
            className="flex-1"
            disabled={disabled}
          />
        </div>

    {showEnd && (
      <>
        <span className="text-sm text-secondary-foreground">-</span>

        <div className="flex gap-2 items-center flex-1 min-w-[200px]">
          {endYear !== PRESENT && (
            <Select value={endMonth} onValueChange={handleEndMonthChange} disabled={disabled}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="End Month" />
              </SelectTrigger>
              <SelectContent className="[&>div]:max-h-[300px] [&>div]:overflow-auto">
                {MONTHS.map((month) => (
                  <SelectItem key={month} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <YearSelect
            value={endYear}
            onValueChange={handleEndYearChange}
            options={endYearOptions}
            placeholder="End Year"
            searchPlaceholder="Search year"
            className="flex-1"
            disabled={disabled}
          />
        </div>
    </>
      )}
      </div>
    </div>
  );
};

